import {useMessageContext} from "../../../contexts/MessageContext";
import {useEffect, useState} from "react";
import {getCompanyKeywords, saveCompanyKeywords} from "../../../services/CompanyService";
import {Box, Button, Chip, Divider, Paper, TextField, Typography} from "@mui/material";
import {Autocomplete} from "@mui/lab";
import SaveIcon from '@mui/icons-material/Save';

const CompanyKeywordsTag = ({company, setLoader}) => {

  const {notificationHandler} = useMessageContext();

  const [companyKeywords, setCompanyKeywords] = useState();

  const [reload, setReload] = useState(false);

  useEffect(() => {
    setLoader(true);
    getCompanyKeywords(company.id)
        .then(res => {
          setCompanyKeywords(res.data);
        }).then(() => setLoader(false))
  }, [reload, company.id, setLoader]);


  const updatePrimaryKeywords = (primaryKeywords) => {
    setCompanyKeywords(prv => {
      return {...prv, primaryKeywords}
    })
  }

  const updateSecondaryKeywords = (secondaryKeywords) => {
    setCompanyKeywords(prv => {
      return {...prv, secondaryKeywords}
    })
  }

  const updateIndustryKeywords = (industryKeywords) => {
    setCompanyKeywords(prv => {
      return {...prv, industryKeywords}
    })
  }

  const updateProductKeywords = (productKeywords) => {
    setCompanyKeywords(prv => {
      return {...prv, productKeywords}
    })
  }

  const updateSeoKeywords = (seoKeywords) => {
    setCompanyKeywords(prv => {
      return {...prv, seoKeywords}
    })
  }

  const updateForbiddenWords = (forbiddenWords) => {
    setCompanyKeywords(prv => {
      return {...prv, forbiddenWords}
    })
  }

  const updateInternalLinking = (internalLinking) => {
    setCompanyKeywords(prv => {
      return {...prv, internalLinking}
    })
  }

  return (
    <Paper
      component="form"
      onSubmit={(event => {
        event.preventDefault();
        setLoader(true);
        companyKeywords.companyId = company.id;
        saveCompanyKeywords(companyKeywords)
            .then(() => setReload(prv => !prv))
            .then(() => notificationHandler.success('Company Saved'))
            .finally(() => setLoader(false))
      })}
      sx={{ mt: 3, p: 3 }}
      elevation={1}
    >
      {companyKeywords && (
        <Box>
          {/* Primary & Secondary Keywords */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
            Primary & Secondary Keywords
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Autocomplete
                multiple
                size="small"
                id="primary-keywords"
                sx={{ flex: 1 }}
                options={[]}
                freeSolo
                value={companyKeywords.primaryKeywords || []}
                onChange={(event, newValue) => {
                  updatePrimaryKeywords(newValue);
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return <Chip key={key} size="small" variant="filled" label={option} {...tagProps} />;
                    })
                }
                renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Primary Keywords" placeholder="Add keyword..." />
                )}
            />
            <Autocomplete
                multiple
                size="small"
                id="secondary-keywords"
                sx={{ flex: 1 }}
                options={[]}
                freeSolo
                value={companyKeywords.secondaryKeywords || []}
                onChange={(event, newValue) => {
                  updateSecondaryKeywords(newValue);
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return <Chip key={key} size="small" variant="filled" label={option} {...tagProps} />;
                    })
                }
                renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Secondary Keywords" placeholder="Add keyword..." />
                )}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Industry & Product Keywords */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
            Industry & Product Keywords
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Autocomplete
                multiple
                size="small"
                id="industry-keywords"
                sx={{ flex: 1 }}
                options={[]}
                freeSolo
                value={companyKeywords.industryKeywords || []}
                onChange={(event, newValue) => {
                  updateIndustryKeywords(newValue);
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return <Chip key={key} size="small" variant="filled" label={option} {...tagProps} />;
                    })
                }
                renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Industry Keywords" placeholder="Add keyword..." />
                )}
            />
            <Autocomplete
                multiple
                size="small"
                id="product-keywords"
                sx={{ flex: 1 }}
                options={[]}
                freeSolo
                value={companyKeywords.productKeywords || []}
                onChange={(event, newValue) => {
                  updateProductKeywords(newValue);
                }}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return <Chip key={key} size="small" variant="filled" label={option} {...tagProps} />;
                    })
                }
                renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Product Keywords" placeholder="Add keyword..." />
                )}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* SEO Keywords */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
            SEO Keywords
          </Typography>

          <Autocomplete
              multiple
              size="small"
              id="seo-keywords"
              options={[]}
              freeSolo
              value={companyKeywords.seoKeywords || []}
              onChange={(event, newValue) => {
                updateSeoKeywords(newValue);
              }}
              renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return <Chip key={key} size="small" variant="filled" label={option} {...tagProps} />;
                  })
              }
              renderInput={(params) => (
                  <TextField {...params} variant="outlined" label="SEO Keywords" placeholder="Add SEO keyword..." />
              )}
              sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 3 }} />

          {/* Content Rules */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase" }}>
            Content Rules
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
                size="small"
                sx={{ flex: 1 }}
                multiline
                minRows={3}
                maxRows={6}
                label="Forbidden Words/Phrases"
                placeholder="Enter forbidden words or phrases separated by commas or new lines"
                variant="outlined"
                value={companyKeywords.forbiddenWords || ''}
                onChange={(event) => {
                  updateForbiddenWords(event.target.value);
                }}
            />
            <TextField
                size="small"
                sx={{ flex: 1 }}
                multiline
                minRows={3}
                maxRows={6}
                label="Internal Linking from Articles"
                placeholder="Enter internal linking guidelines or URLs"
                variant="outlined"
                value={companyKeywords.internalLinking || ''}
                onChange={(event) => {
                  updateInternalLinking(event.target.value);
                }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Actions */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default CompanyKeywordsTag;
